// v2 stonemason — an open-air mason's yard: a timber gantry hoist (two A-frame
// legs under a crossbeam) straddles the banker — the stout dressing table where
// the rough block is squared — with a hoisted block hanging off the gantry rope,
// chiselled blocks stacked about and rubble at the edge. Ported 1:1 from
// age-of-mcp's v2/stonemason.ts, using the library's shared `foundationSlab` and
// `scaffold` helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, benchWork, EFFECT_LOOP, type Part } from "../../lib";

const TIMBER = "#5e4423";   // gantry timber
const WOOD   = "#7a5a30";   // banker top
const STONE  = "#9a9a9a";   // fresh stone
const DARK   = "#6f6f6f";   // dressed/shaded stone
const ROPE   = "#3a2c1c";

const BASE = 0.14;          // foundation slab top

// The banker — the mason's stout dressing table — with the rough block on it.
function banker(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.42, 0.2, 0.34), color: WOOD, position: [0, BASE + 0.1, 0.14] },
    { geometry: new THREE.BoxGeometry(0.26, 0.2, 0.22), color: STONE, position: [0, BASE + 0.3, 0.14], rotation: [0, 0.08, 0] },
  ];
}

// The timber gantry hoist: an upright post on each side (east/west), braced
// from behind, carrying a crossbeam — with a rope and a hoisted block
// hanging over the yard behind the banker.
function gantry(withHoist: boolean): Part[] {
  const postH = 0.64, topY = BASE + postH;
  const parts: Part[] = [];
  for (const x of [-0.3, 0.3]) {
    parts.push(
      { geometry: new THREE.BoxGeometry(0.06, postH, 0.06), color: TIMBER,
        position: [x, BASE + postH / 2, 0] },
      // a raking back-brace from the ground up to the post's mid-height
      { geometry: new THREE.BoxGeometry(0.05, 0.5, 0.05), color: TIMBER,
        position: [x, BASE + 0.21, -0.17], rotation: [-0.6, 0, 0] },
    );
  }
  parts.push({ geometry: new THREE.BoxGeometry(0.74, 0.06, 0.07), color: TIMBER, position: [0, topY + 0.03, 0] });
  if (withHoist) {
    // Rope and hoisted block are named so the "active" animation can wind them
    // up and down (the rope shortens about its top end as the block rises).
    parts.push(
      { geometry: new THREE.BoxGeometry(0.06, 0.08, 0.12), color: DARK, position: [0, topY - 0.04, -0.05] },
      { geometry: new THREE.CylinderGeometry(0.012, 0.012, 0.22, 4), color: ROPE, position: [0, topY - 0.19, -0.1], name: "hoist-rope" },
      { geometry: new THREE.BoxGeometry(0.2, 0.16, 0.16), color: STONE, position: [0, topY - 0.38, -0.1], rotation: [0, 0.3, 0], name: "hoist-load" },
    );
  }
  return parts;
}

// Chiselled blocks stacked about the yard, plus uncut rubble on the west.
function yard(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.2, 0.12, 0.2), color: STONE, position: [0.3, BASE + 0.06, 0.28] },
    { geometry: new THREE.BoxGeometry(0.18, 0.11, 0.18), color: DARK,  position: [0.3, BASE + 0.175, 0.28], rotation: [0, 0.25, 0] },
    { geometry: new THREE.BoxGeometry(0.18, 0.11, 0.18), color: STONE, position: [0.32, BASE + 0.055, 0.06], rotation: [0, -0.2, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.11, 0), color: DARK,  position: [-0.32, BASE + 0.08, 0.3], rotation: [0.2, 0.8, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.08, 0), color: STONE, position: [-0.34, BASE + 0.06, 0.12], rotation: [0.5, 1.6, 0.2] },
  ];
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.86)];
  if (s === 1) return [foundationSlab(0.86), ...banker(), ...scaffold(0.84, 0.55)];
  if (s === 2) return [foundationSlab(0.86), ...banker(), ...gantry(false), ...scaffold(0.84, 0.9)];
  return [foundationSlab(0.86), ...banker(), ...gantry(true), ...yard()];
}

export const stonemasonV2 = defineModel({
  id: "stonemason-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Stonemason (v2)",
  tags: ["building","stonemason","low-poly","age-of-mcp","fable","animated"],
  summary: "Low-poly stonemason.",
  description: "A mason's cutting bench that cuts rough stone into dressed blocks.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      // The gantry hoists the block: it rises 0.14 while the rope winds up
      // (scales about its top end at y = 0.70) to stay taut, then lowers.
      id: "active",
      label: "Hoist working",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [
        {
          target: "hoist-load",
          pivot: [0, 0.40, -0.1],
          position: [
            { t: 0, value: [0, 0, 0] },
            { t: EFFECT_LOOP / 2, value: [0, 0.14, 0], ease: "easeInOut" },
            { t: EFFECT_LOOP, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "hoist-rope",
          pivot: [0, 0.70, -0.1],
          scale: [
            { t: 0, value: [1, 1, 1] },
            { t: EFFECT_LOOP / 2, value: [1, 0.36, 1], ease: "easeInOut" },
            { t: EFFECT_LOOP, value: [1, 1, 1], ease: "easeInOut" },
          ],
        },
      ],
    },
  ],
  stations: [
    { id: "bench-a", position: [0, 0.14, 0.44], facing: Math.PI },
    { id: "bench-b", position: [-0.42, 0.14, 0.06], facing: Math.PI / 2 },
  ],
  interactions: [
    { id: "carve", label: "Carving stone", station: "bench-a", variants: ["complete"], ...benchWork() },
  ],
});
